import { useCallback } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import { PROVIDER_DETAIL_SAMPLE_SIZE } from '../constants/partner-data-gateway.constant';
import {
  Configuration,
  IngestionSummary,
  PaginatedResult,
  ProviderSummary,
} from '../domain/types';
import { summarizeProviderIngestions } from '../utils/provider-summary.utils';
import { useAsyncResource } from './use-async-resource';
import { useConfigurations } from './use-configurations';

export interface UseProviderDetailReturn {
  /** Roll-up over the sample; `null` until both fetches have succeeded. */
  summary: ProviderSummary | null;
  /** Newest ingestions for this bucket, up to `PROVIDER_DETAIL_SAMPLE_SIZE`. */
  sample: IngestionSummary[];
  configurations: Configuration[];
  isLoading: boolean;
  /** True if either fetch failed. */
  hasError: boolean;
  /** Loading/error state of the configurations fetch alone, for the table. */
  configurationsIsLoading: boolean;
  configurationsHasError: boolean;
  /** Re-runs both fetches. */
  refetch: () => void;
}

const EMPTY_SAMPLE_PAGE: PaginatedResult<IngestionSummary> = {
  items: [],
  pageInfo: { hasNextPage: false, nextCursor: null },
};

/**
 * Everything the provider detail page derives from: one unpolled fetch of
 * the bucket's newest files and one of its configurations. The summary is
 * withheld until both are in so the header never claims "no configuration"
 * because the configurations request failed.
 */
export const useProviderDetail = (
  dataProvider: string,
): UseProviderDetailReturn => {
  const client = usePartnerDataClient();
  const fetchSample = useCallback(
    () =>
      client.listIngestions({
        dataProvider,
        limit: PROVIDER_DETAIL_SAMPLE_SIZE,
      }),
    [client, dataProvider],
  );
  const sampleResource = useAsyncResource(fetchSample, EMPTY_SAMPLE_PAGE);
  const configurationsResource = useConfigurations({
    dataProvider,
    limit: PROVIDER_DETAIL_SAMPLE_SIZE,
  });

  const isLoading =
    sampleResource.isLoading || configurationsResource.isLoading;
  const hasError = sampleResource.hasError || configurationsResource.hasError;
  const sample = sampleResource.data.items;
  const { configurations } = configurationsResource;

  const summary =
    isLoading || hasError
      ? null
      : summarizeProviderIngestions({
          dataProvider,
          hasConfiguration: configurations.length > 0,
          ingestions: sample,
          sampleSize: PROVIDER_DETAIL_SAMPLE_SIZE,
        });

  const refetch = (): void => {
    sampleResource.refetch();
    configurationsResource.refetch();
  };

  return {
    summary,
    sample,
    configurations,
    isLoading,
    hasError,
    configurationsIsLoading: configurationsResource.isLoading,
    configurationsHasError: configurationsResource.hasError,
    refetch,
  };
};
